import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DEAL_STAGES, type CrmFunnel as Funnel } from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useI18n } from '../../i18n';
import { formatDate } from '../../lib/format';
import { Card, Loading, PageHeader, StatCard } from '../../components/ui';
import { crmLabel } from '../../lib/crm';

type Period = 'all' | 'week' | 'month';

/** Monday of this week (local), as YYYY-MM-DD. */
function mondayIso(): string {
  const d = new Date(); d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d.toLocaleDateString('en-CA');
}

export function CrmFunnel() {
  const { company } = useAuth();
  const { t, lang } = useI18n();
  const [period, setPeriod] = useState<Period>('all');
  const from = period === 'week' ? mondayIso() : period === 'month' ? new Date(Date.now() - 29 * 86_400_000).toLocaleDateString('en-CA') : '';
  const q = useQuery({ queryKey: ['crm-funnel', company?.id, period], queryFn: () => capi<Funnel>(`/crm/funnel${from ? `?from=${from}` : ''}`) });
  const pct = (n: number | null) => (n === null ? '—' : `${Math.round(n * 100)}%`);

  return (
    <>
      <PageHeader crumb={t('crmCrumb')} title={t('funnelTitle')} sub={t('funnelSub')}
        actions={<div className="pillgroup" role="tablist">
          {(['all', 'month', 'week'] as const).map((p) => (
            <button key={p} type="button" role="tab" aria-selected={period === p} className={`pill sm ${period === p ? 'active' : ''}`} onClick={() => setPeriod(p)}>
              {p === 'all' ? t('periodAll') : p === 'week' ? t('periodWeek') : t('periodMonth')}
            </button>
          ))}
        </div>} />
      {!q.data ? <Loading error={q.error} /> : (
        <div className="stack">
          <div className="grid kpis">
            <StatCard label={t('funnelGoal')} icon="fas fa-flag-checkered" tone="success" value={`${q.data.goal.payingCustomers} / ${q.data.goal.target}`}
              caption={t('funnelGoalCaption', { n: q.data.goal.payingCustomers, target: q.data.goal.target, deadline: formatDate(q.data.goal.deadline, lang) })} />
            <StatCard label={t('funnelWeekly')} icon="fas fa-paper-plane" tone={q.data.weeklyContacts.count >= q.data.weeklyContacts.cap ? 'danger' : 'accent'}
              value={`${q.data.weeklyContacts.count} / ${q.data.weeklyContacts.cap}`} />
          </div>
          <Card>
            <div className="stack md">
              {q.data.lines.map((l, i) => {
                const conv = i > 0 ? q.data.conversions[i - 1] : null;
                return (
                  <div key={l.key}>
                    <div className="row" style={{ justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.86rem' }}>{crmLabel('funnel', l.key, lang)}</span>
                      <span className="small"><strong>{l.count}</strong> <span className="muted">· {t('funnelTarget', { n: l.target })}{conv ? ` · ${t('funnelConversion')} ${pct(conv.rate)}` : ''}</span></span>
                    </div>
                    <div className="bar" aria-hidden="true"><span style={{ width: `${Math.min(100, (l.count / l.target) * 100)}%` }} /></div>
                  </div>
                );
              })}
            </div>
          </Card>
          <div className="grid two-even">
            <Card pad={false} className="table">
              <div className="cardhead"><h2>{t('funnelPipeline')}</h2></div>
              <table><tbody>
                {DEAL_STAGES.map((s) => <tr key={s}><td>{crmLabel('stage', s, lang)}</td><td className="r">{q.data.pipeline[s] ?? 0}</td></tr>)}
              </tbody></table>
            </Card>
            <Card pad={false} className="table">
              <div className="cardhead"><h2>{t('funnelLost')}</h2></div>
              <table><tbody>
                {Object.keys(q.data.lostByReason).length === 0 && <tr className="empty"><td colSpan={2}>{t('crmNone')}</td></tr>}
                {Object.entries(q.data.lostByReason).map(([r, n]) => <tr key={r}><td>{crmLabel('lostReason', r, lang)}</td><td className="r">{n}</td></tr>)}
              </tbody></table>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
